import styled from 'styled-components';
import React, { useEffect, useState } from 'react';
import { IDownloadSubmit } from 'containers/user/CloudManagementModalContainer';
import CustomSpan from 'components/common/text/CustomSpan';
import { T } from 'components/common/text';
import { IconButton } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useCloudManageStore } from 'store/local/cloudManage';

type TDownloadDisk = {
  freeSpaceBytes: number;
  path: string;
  requiredSpaceBytes: number; // root disk가 같으면 downloadDisk, tempDisk 같은 값 다르면 downloadDisk는 totalFileBytes, tempDisk는 maxFileBytes
  rootDisk: string;
};

type TDownloadResStatus =
  | 'job start' // 작업 시작(projectCode, projectId 키 없음)
  | 'disk checking start' // 디스트 용량 체크(다운로드 가능 여부 체크, downloadDisk, tempDisk)
  | 'disk checking completed' // 디스크 용량 체크 완료, 에러(error) 여부 확인(true 시 msg: 'ROOT_DISK_VOLUME_OVER')
  | 'downloading start' // 다운로드 시작(프로젝트 별)
  | 'downloading completed' // 다운로드 종료(프로젝트 별)
  | 'job completed' // 작업 완료 (projectCode, projectId 키 없음)
  | 'job cancelled'; // 작업 취소 (projectCode, projectId 키 없음)

interface IDownloadRes {
  downloadId: string;
  status: TDownloadResStatus; // 현재 작업 단계
  // disk checking start&complete //
  isSameDrive: boolean; // 다운로드 받을 루트 디스크와 임시 다운로드 디스크 동일 여부
  downloadDisk: TDownloadDisk; // 다운로드 받을 디스크 정보
  tempDisk: TDownloadDisk; // 임시 다운로드 받을 디스크 정보
  // dowloading start&complete //
  projectIdx: number; // 현재 작업 프로젝트(키 없을 수 있음)
  projectId: string; // 현재 작업 프로젝트 ID(키 없을 수 있음)
  // ************************* //
  totalProjectCount: number; // 전체 작업 프로젝트 개수
  currenProjectCount: number; // 현재 작업중인 프로젝트 순서
  downloadPath: string; // 다운로드 경로
  error: boolean; // 에러 여부
  percent: number; // 현재 작업 퍼센트
  totalFileBytes: number; // 선택한 다운로드 받을 프로젝트 총 메가바이트
  maxFileBytes: number; // 선택한 다운로드 받을 프로젝트 중 가장 큰 메가 바이트(임시 파일 다운로드 용량 체크 때문에)
  completedZipFileList: string[]; // 현재까지 생성한 압푹파일 리스트
  completedProjectIdxList: number[]; // 다운로드 완료한 프로젝트 인덱스 리스트
  msg: string;
  // DOWNLOAD_CANCELLED_BY_USER: '작업 취소'
  // NETWORK_ERROR_${response_status}: API 네트워크 에러
  // ZIP_CREATION_FAIL: '압축파일 생성 실패'
  // ROOT_DISK_VOLUME_OVER
}

type TDownloadStatus =
  | 0 // 작업 완료
  | 1 // 다운로드 준비 (준비,디스크 체크 완료)
  | 2 // 다운로드 중 (시작,완료)
  | 3 // 작업 취소
  | 4 // 20개 초과
  | 5 // 디스크부족
  | 6; // Error;

interface IDownloadProgressRes {
  bytes: number;
  fileName: string;
  percent: number;
  projectId: string;
  projectIdx: number;
  totalBytes: number;
}

interface CloudDownloadModalProps {
  isListOver: boolean;
  downloadData: IDownloadSubmit | null;
  onCloseModal: () => void;
  onOpenWindow: () => void;
}

const CloudDownloadModal = ({
  isListOver,
  downloadData,
  onCloseModal,
  onOpenWindow,
}: CloudDownloadModalProps) => {
  const { ipcRenderer } = window;
  const { setCompleteDownloadList } = useCloudManageStore();

  const { t } = useTranslation();
  const [downloadStatus, setDownloadStatus] = useState<TDownloadStatus>(1);
  const [errorCode, setErrorCode] = useState<string>('');
  const [downloadedFile, setDownloadedFile] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const [title, setTitle] = useState<string>(t('CLOUD_DOWNLOAD'));
  const [closeReady, setCloseReady] = useState<boolean>(false);
  const [downloadId, setDownloadId] = useState<string>('');

  const handleRequestDownload = (data: IDownloadSubmit) => {
    setTimeout(() => {
      ipcRenderer.send('multipleProjectFileDownloadToLocal', data);
    }, 500);
  };

  const handleClickDownloadStop = async (downloadId: string) => {
    console.log('HDS] handleClickDownloadStop _______ ', downloadId);
    const result = await ipcRenderer.invoke('cancelMultipleProjectFileDownloadToLocalAsync', {
      downloadId: downloadId,
    });
  };

  useEffect(() => {
    if (isListOver) {
      setTitle(t('CLOUD_UNABLE_DOWNLOAD'));
      setDownloadStatus(4);
      setCloseReady(true);
    }
  }, [isListOver]);

  useEffect(() => {
    if (!downloadData) return;
    handleRequestDownload(downloadData);
  }, [downloadData]);

  useEffect(() => {
    if (!ipcRenderer || !!isListOver) return;
    ipcRenderer.on('multipleProjectFileDownloadToLocalRes', (e: any, data: IDownloadRes) => {
      console.log('HDS] multipleProjectFileDownloadToLocalRes ___________', data);
      setDownloadId(data.downloadId);
      if (data.status === 'job completed') {
        setTimeout(() => {
          setDownloadStatus(0);
          setDownloadedFile('');
          setCloseReady(true);
        }, 500);
      }
      if (data.status === 'downloading start' || data.status === 'downloading completed') {
        setDownloadStatus(2);
      }

      if (data.msg) {
        if (data.msg === 'DOWNLOAD_CANCELLED_BY_USER') {
          setDownloadStatus(3);
          onCloseModal();
        } else if (data.msg === 'ROOT_DISK_VOLUME_OVER') {
          setTitle(t('CLOUD_LOCAL_DISK_OVER'));
          setDownloadStatus(5);
        } else {
          setTitle(t('CLOUD_UNABLE_DOWNLOAD'));
          setErrorCode(data.msg);
          setDownloadStatus(6);
        }
        setCloseReady(true);
      }
      // 다운로드 프로그레스
      setProgress(data.percent);

      // 완료목록 상시 업데이트
      setCompleteDownloadList(prev => [...new Set([...prev, ...data.completedProjectIdxList])]);
    });
  }, [isListOver]);

  useEffect(() => {
    if (!ipcRenderer) return;
    ipcRenderer.on(
      'multipleProjectFileDownloadToLocalProgress',
      (e: any, data: IDownloadProgressRes) => {
        // console.log('HDS] multipleProjectFileDownloadToLocalProgress ___________', data);
        setDownloadedFile(`${data.projectId}/${data.fileName}`);
      },
    );
  }, []);

  return (
    <StyledCloudDownloadModal data-component-name="CloudDownloadModal" $progress={progress}>
      <div className="cloudDownloadModal__title_box">
        <CustomSpan>{title}</CustomSpan>
      </div>
      <div className="cloudDownloadModal__contents_box">
        {downloadStatus === 0 && (
          <div className="cloudDownloadModal__text">
            <CustomSpan fontSize={16} fontWeight={400}>
              <T>CLOUD_DOWNLOAD_COMPETE_TEXT</T>
            </CustomSpan>
          </div>
        )}
        {downloadStatus === 1 && (
          <div className="cloudDownloadModal__text">
            <CustomSpan fontSize={16} fontWeight={400}>
              <T>CLOUD_LOCAL_DISK_CHECK</T>
            </CustomSpan>
            <CustomSpan fontSize={16} fontWeight={400}>
              {`${progress}%`}
            </CustomSpan>
          </div>
        )}
        {downloadStatus === 2 && (
          <div className="cloudDownloadModal__text">
            <CustomSpan fontSize={16} fontWeight={400}>
              <T>CLOUD_DOWNLOAD_MSG</T>
            </CustomSpan>
            <CustomSpan fontSize={16} fontWeight={400}>
              {`${progress}%`}
            </CustomSpan>
          </div>
        )}
        {downloadStatus === 4 && (
          <div className="cloudDownloadModal__text">
            <CustomSpan fontSize={16} fontWeight={400}>
              <T>CLOUD_SELECT_OVER_TEXT</T>
            </CustomSpan>
          </div>
        )}
        {downloadStatus === 5 && (
          <div className="cloudDownloadModal__text">
            <CustomSpan fontSize={16} fontWeight={400}>
              <T>CLOUD_LOCAL_DISK_OVER_TEXT</T>
            </CustomSpan>
          </div>
        )}
        {downloadStatus === 6 && (
          <div className="cloudDownloadModal__text">
            <CustomSpan fontSize={16} fontWeight={400}>
              <T>GLOBAL_ERROR</T> : {errorCode}
            </CustomSpan>
          </div>
        )}
        {!closeReady && (
          <div className="cloudDownloadModal__progress_wrapper">
            <div className="cloudDownloadModal__progress_box">
              <div className="cloudDownloadModal__progress"></div>
            </div>
            <div className="cloudDownloadModal__progress_file">
              <CustomSpan fontSize={16} fontWeight={400}>
                {downloadedFile}
              </CustomSpan>
            </div>
          </div>
        )}
      </div>
      <div className="cloudDownloadModal__button_box">
        {closeReady ? (
          <>
            <IconButton className="cloudDownloadModal__button open_window" onClick={onOpenWindow}>
              <CustomSpan fontSize={16} fontWeight={400}>
                <T>GLOBAL_SAVE_PATH_OPEN</T>
              </CustomSpan>
            </IconButton>
            <IconButton className="cloudDownloadModal__button error" onClick={onCloseModal}>
              <CustomSpan fontSize={16} fontWeight={400}>
                <T>GLOBAL_CONFIRM</T>
              </CustomSpan>
            </IconButton>
          </>
        ) : (
          <IconButton
            className="cloudDownloadModal__button stop"
            onClick={() => {
              handleClickDownloadStop(downloadId);
            }}
          >
            <CustomSpan fontSize={16} fontWeight={400}>
              <T>GLOBAL_STOP</T>
            </CustomSpan>
          </IconButton>
        )}
      </div>
    </StyledCloudDownloadModal>
  );
};

const StyledCloudDownloadModal = styled.div<{
  $progress: number;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 40px;
  .cloudDownloadModal__title_box {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 64px;
    padding: 20px;
    font-size: 20px;
    font-weight: 400;
    color: #222;
  }
  .cloudDownloadModal__contents_box {
    height: 50px;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    row-gap: 8px;
    .cloudDownloadModal__text {
      display: flex;
      justify-content: space-between;
    }
    .cloudDownloadModal__progress_wrapper {
      display: flex;
      flex-direction: column;
      row-gap: 8px;
      .cloudDownloadModal__progress_box {
        width: 100%;
        height: 16px;
        border-radius: 8px;
        overflow: hidden;
        background-color: #e1e1e7;
        .cloudDownloadModal__progress {
          width: ${({ $progress }) => `${$progress}%`};
          transition: width 0.3s ease;
          height: 16px;
          background: linear-gradient(to right, #e1e1e7, #00a6e2);
        }
      }

      .cloudDownloadModal__progress_file {
        width: 100%;
        display: inline-block;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }
  }
  .cloudDownloadModal__button_box {
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    column-gap: 10px;
    .cloudDownloadModal__button {
      width: 156px;
      height: 40px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 20px;
      border: 1px solid #d1d1d1;
    }
  }
`;

export default CloudDownloadModal;
